import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getS3URL } from "../../_helpers";

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const MiniCourseSkeleton = () => (
  <div className="bg-white rounded-2xl p-4 flex items-center gap-4 animate-pulse relative overflow-hidden h-[90px] mb-3.5">
    <div className="w-16 h-16 bg-slate-200 rounded-xl flex-shrink-0 relative overflow-hidden">
      <div className="shimmer-sweep" />
    </div>
    <div className="flex-1 space-y-2.5">
      <div className="h-3.5 bg-slate-200 rounded w-1/3 relative overflow-hidden">
        <div className="shimmer-sweep" />
      </div>
      <div className="h-5 bg-slate-200 rounded w-3/4 relative overflow-hidden">
        <div className="shimmer-sweep" />
      </div>
      <div className="h-3.5 bg-slate-200 rounded w-1/2 relative overflow-hidden">
        <div className="shimmer-sweep" />
      </div>
    </div>
  </div>
);

const TrendingCategoryColumn = ({ title, courses, onCategoryClick, onCourseClick, isLoading }) => {
  return (
    <div className="bg-gradient-to-b from-[#f3f7fd] to-[#f8fafd] border border-[#e4eeff]/40 rounded-[2rem] p-6 flex flex-col justify-between min-h-[380px] shadow-sm">
      <div>
        {/* Category Header Link */}
        <div 
          onClick={onCategoryClick}
          className="text-slate-900 font-black text-xl sm:text-2xl mb-6 flex items-center justify-between cursor-pointer hover:text-blue-600 transition-colors select-none group w-fit"
        >
          <span className="flex items-center gap-2">
            <span>{title}</span>
            <svg 
              className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1.5 transition-all duration-300" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth="3.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>

        {/* Courses list */}
        <div className="flex flex-col gap-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => <MiniCourseSkeleton key={i} />)
          ) : courses && courses.length > 0 ? (
            courses.map((course) => {
              const partnerName = course.companyName || 
                (course.CourseName?.toLowerCase().includes('google') ? 'Google' : 
                 course.CourseName?.toLowerCase().includes('microsoft') ? 'Microsoft' : 
                 course.CourseName?.toLowerCase().includes('ibm') ? 'IBM' : 'SimkLearn');
              
              const partnerLogo = partnerName === 'Google' 
                ? 'https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg'
                : partnerName === 'Microsoft'
                ? 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg'
                : partnerName === 'IBM'
                ? 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg'
                : null;

              return (
                <div
                  key={course.id}
                  onClick={() => onCourseClick(course)}
                  className="bg-white rounded-2xl p-4 flex items-center gap-4 hover:shadow-md hover:scale-[1.01] transition-all duration-300 cursor-pointer group/row border border-slate-100/70 hover:border-slate-200/50"
                >
                  {/* Thumbnail Image */}
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 border border-slate-100">
                    <img 
                      src={course?.imageFiles?.[0]?.fileName ? getS3URL(course.imageFiles[0].fileName) : "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg"} 
                      alt={course.CourseName} 
                      className="w-full h-full object-cover group-hover/row:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg";
                      }}
                    />
                  </div>

                  {/* Course Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-bold">
                      {partnerLogo && (
                        <img src={partnerLogo} alt={partnerName} className="w-4 h-4 object-contain" />
                      )}
                      <span className="truncate">{partnerName}</span>
                    </div>

                    <h4 className="font-extrabold text-sm sm:text-base text-slate-900 leading-snug line-clamp-1 mt-1 group-hover/row:text-blue-600 transition-colors duration-200">
                      {course.CourseName}
                    </h4>

                    <div className="flex items-center gap-2.5 text-xs text-slate-400 font-semibold mt-1.5">
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                        {course.courseType || "Specialization"}
                      </span>
                      <div className="flex items-center gap-0.5 text-amber-500 font-black">
                        <span>★</span>
                        <span>{course.rating || "4.8"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center p-6 bg-white/40 rounded-2xl text-center border border-white/50 min-h-[220px]">
              <span className="text-xl mb-1">🔍</span>
              <p className="text-xs text-slate-500 font-semibold">No courses listed.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function TrendingSearches() {
  const navigate = useNavigate();
  const { isLoading: categoryLoading } = useSelector(state => state?.category || {});
  const { courses, isFetching: courseLoading } = useSelector(state => state?.course || []);
  const courseList = courses?.all?.courses || [];

  const isLoading = categoryLoading || courseLoading;

  // Filter dynamic course listings under top category search names
  const pythonCourses = courseList.filter(
    (c) => c.CourseName?.toLowerCase().includes('python') || c.category?.toLowerCase().includes('AI')
  ).slice(0, 3);

  const dataCourses = courseList.filter(
    (c) => c.CourseName?.toLowerCase().includes('Data') || c.CourseName?.toLowerCase().includes('Analytics') || c.category?.toLowerCase().includes('data')
  ).slice(0, 3);

  const pmCourses = courseList.filter(
    (c) => c.CourseName?.toLowerCase().includes('Data') || c.CourseName?.toLowerCase().includes('science') || c.category?.toLowerCase().includes('business')
  ).slice(0, 3);

  const handleCategoryClick = (categoryName) => {
    navigate(`/search-new?new-item-type=${encodeURIComponent(categoryName)}`);
  };

  const handleCourseClick = (course) => {
    const slug = slugify(course.CourseName);
    navigate(`/${slug}`, { state: { course } });
  };

  return (
    <div className="w-full mt-14 px-4 md:px-6 text-gray-900 select-none">
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .shimmer-sweep {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.4) 50%,
            transparent 100%
          );
          animation: shimmer 1.5s infinite linear;
        }
      `}</style>

      {/* Header section title */}
      <h2 className="text-3xl font-black tracking-tight text-gray-900 mb-6 px-2">
        Trending searches
      </h2>

      {/* 3-Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <TrendingCategoryColumn 
          title="AI & Machine Learning"
          courses={pythonCourses}
          isLoading={isLoading}
          onCategoryClick={() => handleCategoryClick("AI & Machine Learning")}
          onCourseClick={handleCourseClick}
        />
        
        <TrendingCategoryColumn 
          title="Data Analytics"
          courses={dataCourses}
          isLoading={isLoading}
          onCategoryClick={() => handleCategoryClick("Data Analytics")}
          onCourseClick={handleCourseClick}
        />

        <TrendingCategoryColumn 
          title="Data science"
          courses={pmCourses}
          isLoading={isLoading}
          onCategoryClick={() => handleCategoryClick("Data science")}
          onCourseClick={handleCourseClick}
        />
      </div>
    </div>
  );
}

export default TrendingSearches;
